<template>
    <template v-if="paymentConfig">
        <CenteredContainer v-if="screenOrientation === 'vertical' || isScanFlow" class="vertical">
            <template v-if="isScanFlow">
                <div class="scan-flow">
                    <h3 class="scan-heading">
                        {{ $t('scanFlowHeading') }} {{ formatCurrency(grandTotal) }}
                    </h3>
                    
                    <div class="reader-animation-container" aria-hidden="true">
                        <div class="badge-track">
                            <SvgIcon 
                                :name="badgeIconName" 
                                width="14rem" 
                                height="14rem" 
                                class="badge-icon badge-move"
                            />
                        </div>
                        <div class="reader-base">
                            <svg viewBox="0 0 80 192" class="reader-svg">
                                <defs>
                                    <linearGradient id="bodyGrad" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stop-color="#1a1a1a"/>
                                        <stop offset="50%" stop-color="#2a2a2a"/>
                                        <stop offset="100%" stop-color="#1a1a1a"/>
                                    </linearGradient>
                                </defs>
                                <!-- Outer shell -->
                                <rect x="6" y="4" width="68" height="184" rx="8" fill="#0a0a0a"/>
                                <!-- Front face -->
                                <rect x="10" y="8" width="60" height="176" rx="6" fill="url(#bodyGrad)"/>
                                <!-- LED bar at top (red idle, green on scan) -->
                                <rect x="20" y="16" width="40" height="5" rx="2.5" class="reader-led" fill="#ef4444"/>
                                <!-- Scan zone recessed area -->
                                <rect x="16" y="36" width="48" height="140" rx="4" fill="#111" stroke="#333" stroke-width="1"/>
                            </svg>
                        </div>
                    </div>

                    <p class="scan-instruction" v-if="scanInstructionText">
                        {{ scanInstructionText }}
                    </p>

                    <div class="cta-zone">
                        <button
                            class="btn btn-lg btn-primary scan-cta"
                            :class="{ 'is-ready': ctaReady, loading: !ctaReady }"
                            :disabled="!ctaReady"
                            @click="submit"
                        >
                            <span class="cta-label">
                                Submit Order
                                <SvgIcon name="forward" width="3rem" height="3rem" />
                            </span>
                        </button>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="top">
                    <h3>{{ paymentConfig.name }}</h3>
                    <div class="payment-total">{{ formatCurrency(grandTotal) }}</div>
                    <div class="payment-taxes">{{ paymentDetails }}</div>
                </div>
                <div class="middle" v-if="paymentConfig.icon">
                    <SvgIcon :name="paymentConfig.icon" width="38.625rem" height="38.625rem" />
                </div>
                <div class="bottom">
                    <p class="instructions">{{ paymentConfig.instructions }}</p>
                    <br /><br />
                    <button class="btn btn-lg btn-primary d-block w-100 right" @click="submit">
                        {{ $t('submitButtonText') }}
                        <SvgIcon name="forward" width="3rem" height="3rem" />
                    </button>
                </div>
            </template>
        </CenteredContainer>
        <div v-else>
            <!-- Landscape Mode -->
            <TwoPartLayout>
                <template v-slot:one>
                    <template v-if="isScanFlow">
                        <div class="reader-animation-container landscape-mode" aria-hidden="true">
                            <div class="badge-track">
                                <SvgIcon 
                                    :name="badgeIconName" 
                                    width="14rem" 
                                    height="14rem" 
                                    class="badge-icon badge-move"
                                />
                            </div>
                            <div class="reader-base">
                                <svg viewBox="0 0 80 192" class="reader-svg">
                                    <defs>
                                        <linearGradient id="bodyGradL" x1="0" y1="0" x2="1" y2="0">
                                            <stop offset="0%" stop-color="#1a1a1a"/>
                                            <stop offset="50%" stop-color="#2a2a2a"/>
                                            <stop offset="100%" stop-color="#1a1a1a"/>
                                        </linearGradient>
                                    </defs>
                                    <rect x="6" y="4" width="68" height="184" rx="8" fill="#0a0a0a"/>
                                    <rect x="10" y="8" width="60" height="176" rx="6" fill="url(#bodyGradL)"/>
                                    <rect x="20" y="16" width="40" height="5" rx="2.5" class="reader-led" fill="#ef4444"/>
                                    <rect x="16" y="36" width="48" height="140" rx="4" fill="#111" stroke="#333" stroke-width="1"/>
                                </svg>
                            </div>
                        </div>
                    </template>
                    <SvgIcon v-else-if="paymentConfig.icon" :name="paymentConfig.icon" width="38.625rem" height="38.625rem" />
                </template>
                <template v-slot:two>
                    <template v-if="isScanFlow">
                        <div class="scan-flow landscape">
                            <h3 class="scan-heading">
                                {{ $t('scanFlowHeading') }} {{ formatCurrency(grandTotal) }}
                            </h3>
                            <div class="payment-taxes">{{ paymentDetails }}</div>
                            <p class="scan-instruction" v-if="scanInstructionText">
                                {{ scanInstructionText }}
                            </p>
                            <div class="cta-zone">
                                <button
                                    class="btn btn-lg btn-primary scan-cta"
                                    :class="{ 'is-ready': ctaReady, loading: !ctaReady }"
                                    :disabled="!ctaReady"
                                    @click="submit"
                                >
                                    <span class="cta-label">
                                        Submit Order
                                        <SvgIcon name="forward" width="3rem" height="3rem" />
                                    </span>
                                </button>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="totals align-items-start">
                            <h3>{{ paymentConfig.name }}</h3>
                            <div class="payment-total">{{ formatCurrency(grandTotal) }}</div>
                            <div class="payment-taxes">{{ paymentDetails }}</div>
                        </div>
                        <div class="instructions align-items-start">
                            <p class="instructions">{{ paymentConfig.instructions }}</p>
                            <br /><br />
                            <button class="btn btn-lg btn-primary d-block w-100 right" @click="submit">
                                {{ $t('submitButtonText') }}
                                <SvgIcon name="forward" width="3rem" height="3rem" />
                            </button>
                        </div>
                    </template>
                </template>
            </TwoPartLayout>
        </div>
    </template>
</template>
<script>
import { mapGetters } from "vuex";
import { formatCurrency, paymentDetailsAsString } from "@/utils/helpers";
import TwoPartLayout from "@/components/global/TwoPartLayout.vue";
import CenteredContainer from "@/components/global/CenteredContainer.vue";
export default {
    components: {
        TwoPartLayout,
        CenteredContainer
    },
    data() {
        return {
            ctaReady: false,
            ctaTimer: null,
        };
    },
    props: {
        paymentConfig: {
            type: Object,
            required: true,
        }
    },
    emits: ["submit", "cancel"],
    methods: {
        formatCurrency,
        handleBack() {
            this.$emit("cancel");
        },
        submit() {
            this.$emit("submit");
        },
    },
    computed: {
        ...mapGetters(["screenOrientation"]),
        ...mapGetters("location", ["config"]),
        ...mapGetters("order", ["taxTotal", "subtotal", "grandTotal", "coupon", "discounts", "surcharges"]),
        paymentDetails() {
            return paymentDetailsAsString(this.subtotal, this.taxTotal, 0, this.discounts, this.surcharges);
        },
        isScanFlow() {
            const featureEnabled = this.config?.useBadgeScanUI === true;
            const nameContainsBadgeScan = (this.paymentConfig?.name || '')
                .toLowerCase()
                .includes('badge scan');
            return featureEnabled && nameContainsBadgeScan;
        },
        scanInstructionText() {
            if (this.ctaReady) return 'Tap Submit Order to complete.';

            const instructions = this.paymentConfig?.instructions?.trim();
            if (instructions) return instructions;
            // Fallback if no instructions provided
            return this.$t('scanFlowInstruction') || 'Scan your badge then tap Submit Order to complete.';
        },
        badgeIconName() {
            const icon = this.paymentConfig?.icon;
            // If the icon is 'badge', remap to 'employee' as 'badge' isn't in svgLib
            if (icon === 'badge') return 'employee';
            // Default to 'employee' if missing, otherwise use the config icon
            return icon || 'employee';
        },
    },
    mounted() {
        if (this.isScanFlow) {
            this.ctaTimer = setTimeout(() => {
                this.ctaReady = true;
            }, 2000);
        }
    },
    beforeUnmount() {
        if (this.ctaTimer) clearTimeout(this.ctaTimer);
    },
};
</script>
<style scoped>
/* ===== Non-scan flow (unchanged) ===== */
.payment-total {
    font-size: 6rem;
    font-weight: 700;
    color: var(--primary-color);
}

.payment-taxes {
    font-size: 1.5rem;
    color: #666;
}

.instructions {
    margin-top: 2rem;
    font-size: 2.125rem;
    line-height: 3rem;
}

.right .svg-icon {
    margin-left: 1rem;
}

.vertical {
    padding: 0 2rem;
}

.vertical .top {
    text-align: center !important;
    margin-bottom: 6rem;
}

.vertical * {
    text-align: center;
}

.vertical .middle {
    margin-bottom: 2rem;
}

.vertical .bottom {
    text-align: center;
    margin-bottom: 2rem;
    width: 40vw;
}

/* ===== Scan flow ===== */
.scan-flow {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.scan-heading {
    font-size: 2.5rem;
    font-weight: 700;
    color: #111;
    margin-bottom: 0.5rem;
}

/* Animation Container — side-by-side: badge on left, scanner on right */
.reader-animation-container {
    position: relative;
    width: 32rem;
    height: 20rem;
    margin: 1.5rem auto 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Badge floats to the left of the scanner and taps toward it */
.badge-track {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    pointer-events: none;
}

.badge-icon {
    color: #374151;
    filter: drop-shadow(0 0.2rem 0.5rem rgba(0,0,0,0.25));
    display: block;
}

.badge-move {
    animation: badge-tap 2.4s ease-in-out infinite;
}

@keyframes badge-tap {
    0%, 100% {
        transform: translateX(0) rotate(-8deg);
    }
    35%, 55% {
        transform: translateX(6rem) rotate(-2deg);
    }
}

/* Reader (scanner) — wall-mounted, no stand */
.reader-base {
    position: relative;
    width: 6.5rem;
    height: 16.5rem;
    margin-left: 12rem;
    z-index: 2;
}

.reader-svg {
    width: 100%;
    height: 100%;
    display: block;
}

/* LED bar — off by default, flashes red on badge tap */
.reader-led {
    animation: led-scan 2.4s ease-in-out infinite;
}

@keyframes led-scan {
    0%, 33% {
        fill: #333;
    }
    36% {
        fill: #ef4444;
    }
    54% {
        fill: #ef4444;
    }
    58% {
        fill: #333;
    }
    100% {
        fill: #333;
    }
}

.scan-instruction {
    font-size: 1.65rem;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.05rem;
    margin-bottom: 1.5rem;
    max-width: 80%;
}

/* CTA zone */
.cta-zone {
    width: 100%;
    max-width: 40rem;
    margin: 0 auto;
}

.scan-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    position: relative;
    overflow: hidden;
    width: 100%;
    padding: 1.5rem 2rem;
    font-size: 1.75rem;
    font-weight: 700;
    border-radius: 3rem; /* Pill shape */
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    background-color: #222; /* Default disabled/dark state */
    border: none;
    color: #fff;
}

.scan-cta:disabled {
    opacity: 0.7;
    background-color: #333;
    cursor: wait;
}

.scan-cta.loading::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: -38%;
    width: 38%;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.32), rgba(255, 255, 255, 0));
    animation: cta-sweep 1.1s linear infinite;
}

.scan-cta.loading .cta-label {
    position: relative;
    z-index: 1;
}

.scan-cta.is-ready {
    background-color: var(--primary-color);
    transform: scale(1.02);
    box-shadow: 0 0.5rem 2rem rgba(0,0,0,0.2);
}

@keyframes cta-sweep {
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(360%);
    }
}

/* Landscape Tweaks */
.landscape-mode {
    margin: 0 auto;
}

.scan-flow.landscape {
    align-items: flex-start;
    text-align: left;
    padding-left: 2rem;
}

.scan-flow.landscape .cta-zone {
    margin: 0;
}

@media (prefers-reduced-motion: reduce) {
    .badge-move, .reader-led {
        animation: none;
    }
    .badge-move {
        transform: translateX(6rem) rotate(-2deg);
    }
    .reader-led {
        fill: #ef4444;
    }
    .scan-cta.loading::before {
        animation: none;
        opacity: 0.18;
        left: 31%;
    }
}
</style>
